/**
 * the available attributes for the meta element
 * @export
 * @enum MetaAttributeKey
 */
export enum MetaAttributeKey {
  NAME = 'name',
  PROPERTY = 'property',
}

/**
 * elements that are used for meta tags manipulation
 */
export enum MetaElements {
  META = 'meta',
  TITLE = 'title',
  HEAD = 'head',
  LINK = 'link',
}

/**
 * structure of the MetaTag attribute to be used in the meta element
 * example: <meta name="description" content="my meta tag">
 * @export
 * @interface MetaAttribute
 */
export type MetaAttribute = {
  metaValue: string;
  key: MetaAttributeKey;
};

/**
 * default structure for a MetaTag.
 * the attribute defines what meta tag should be changed
 * the content represents the content of the meta tag
 * @export
 * @interface MetaTag
 */
export type AttributeMetaTag = {
  attribute: MetaAttribute;
  content: string;
};

export type LinkTagAttribute = {
  metaValue: string;
  key: 'rel'; // other keys might be added in future.
};

export type LinkTag = {
  attribute: LinkTagAttribute[];
  href: string;
};

export type Head = {
  title?: string;
  meta?: AttributeMetaTag[];
  link?: LinkTag[];
};

/**
 * A schema.org node, as consumed by `<script type="application/ld+json">`.
 * Intentionally untyped beyond an object: schema.org shapes differ per component
 * and each team already models the ones it needs.
 */
export type JsonLdSchema = Record<string, unknown>;

/**
 * A structured data payload registered on the SSR context, for the renderer to emit.
 * An `id` is optional: when present it becomes the node's deduplication key and is
 * emitted as `data-json-ld`; when absent the node is deduplicated by its contents.
 */
export type StructuredDataEntry = {
  id?: string;
  schema: JsonLdSchema;
};

export type UseJsonLdOptions = {
  /**
   * Optional stable deduplication key. When omitted, nodes dedupe by content. As a
   * shared key, the same `id` on two declarations (e.g. a loader and a component) merges
   * them into one node; an `id` on only one side never merges and can duplicate.
   */
  id?: string;
};
